import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
    AssetLink,
    ContentfulArrayContract,
    ContentfulAssetContract,
    ContentfulAssetFields,
    ContentfulEntry,
    ContentfulEntryContract,
    ContentfulEntryContractFields,
    ContentfulEntryFromContract,
    EntryLink,
    isAssetLink,
    isEntryLink,
    isRichTextNodeContract,
    LinkObject,
    RichTextNode,
    RichTextNodeContract,
} from '../types/content-types/contracts';
import {
    CheckInMessage,
    CheckInMessageContent,
    CheckInMessageContract,
} from '../types/content-types/TypeCheckInMessage';
import {
    LandingPage,
    LandingPageContract,
} from '../types/content-types/TypeLandingPage';
import {
    PrivacyPolicy,
    PrivacyPolicyContract,
} from '../types/content-types/TypePrivacyPolicy';
import {
    ReleaseNotes,
    ReleaseNotesContract,
} from '../types/content-types/TypeReleaseNotes';
import {
    SupportedDevice,
    SupportedDeviceContract,
} from '../types/content-types/TypeSupportedDevice';
import {
    TrailNews,
    TrailNewsContract,
} from '../types/content-types/TypeTrailNews';
import {
    ContentQuery,
    ContentResourceService,
} from './content-resource.service';

@Injectable({
    providedIn: 'root',
})
export class ContentService {
    constructor(private contentResourceService: ContentResourceService) {}

    getAll<T extends ContentfulEntryContract>(
        query: ContentQuery
    ): Observable<ContentfulEntryFromContract<T>[]> {
        return this.contentResourceService
            .getEntries<ContentfulArrayContract<T>>(query)
            .pipe(
                map(
                    (entries) =>
                        entries.items.map((item) =>
                            this.resolveLinks(
                                item,
                                entries.includes?.Asset ?? [],
                                entries.includes?.Entry ?? []
                            )
                        ) as ContentfulEntryFromContract<T>[]
                )
            );
    }

    getById<T extends ContentfulEntryContract>(id: string): Observable<T> {
        return this.contentResourceService.getEntryById<T>(id);
    }

    getSingle<T extends ContentfulEntryContract>(
        query: ContentQuery
    ): Observable<ContentfulEntryFromContract<T>> {
        return this.getAll<T>(query).pipe(map((items) => items[0]));
    }

    getCheckInMessage(id: string): Observable<CheckInMessage> {
        return this.getById<CheckInMessageContract>(id).pipe(
            map(
                (content) =>
                    new CheckInMessage(content as CheckInMessageContent)
            )
        );
    }

    getLandingPage(urlName: string): Observable<LandingPage> {
        return this.getSingle<LandingPageContract>({
            content_type: 'landingPage',
            'fields.urlName': urlName,
        }).pipe(map((content) => new LandingPage(content)));
    }

    getPrivacyPolicy(): Observable<PrivacyPolicy> {
        return this.getSingle<PrivacyPolicyContract>({
            content_type: 'privacyPolicy',
        }).pipe(map((content) => new PrivacyPolicy(content)));
    }

    getReleaseNotes(id: string): Observable<ReleaseNotes | undefined> {
        return this.getSingle<ReleaseNotesContract>({
            content_type: 'releaseNotes',
            'fields.version': id,
        }).pipe(
            map((content) => (content ? new ReleaseNotes(content) : undefined))
        );
    }

    getSupportedDevices(): Observable<SupportedDevice[]> {
        return this.getAll<SupportedDeviceContract>({
            content_type: 'supportedDevice',
        }).pipe(
            map((content) =>
                content.map((device) => new SupportedDevice(device))
            )
        );
    }

    getTrailNews(): Observable<TrailNews> {
        return this.getSingle<TrailNewsContract>({
            content_type: 'trailNews',
        }).pipe(map((content) => new TrailNews(content)));
    }

    renderRichText(doc: RichTextNode): string {
        if (!doc || !doc.content) {
            return '';
        }

        return this.processRichTextNode(doc);
    }

    private processRichTextNode(node: RichTextNode): string {
        if (!node) return '';

        switch (node.nodeType) {
            case 'document':
                return (
                    node.content
                        ?.map((child: RichTextNode) =>
                            this.processRichTextNode(child)
                        )
                        .join('') || ''
                );

            case 'paragraph':
                return `<p>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</p>`;

            case 'heading-1':
                return `<h1>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</h1>`;

            case 'heading-2':
                return `<h2>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</h2>`;

            case 'heading-3':
                return `<h3>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</h3>`;

            case 'heading-4':
                return `<h4>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</h4>`;

            case 'heading-5':
                return `<h5>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</h5>`;

            case 'heading-6':
                return `<h6>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</h6>`;

            case 'unordered-list':
                return `<ul>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</ul>`;

            case 'ordered-list':
                return `<ol>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</ol>`;

            case 'list-item':
                return `<li>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</li>`;

            case 'blockquote':
                return `<blockquote>${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</blockquote>`;

            case 'hr':
                return '<hr>';

            case 'text': {
                let text = node.value || '';

                if (node.marks) {
                    node.marks.forEach((mark) => {
                        switch (mark.type) {
                            case 'bold':
                                text = `<b>${text}</b>`;
                                break;
                            case 'italic':
                                text = `<em>${text}</em>`;
                                break;
                            case 'underline':
                                text = `<u>${text}</u>`;
                                break;
                            case 'code':
                                text = `<code>${text}</code>`;
                                break;
                        }
                    });
                }

                return text;
            }

            case 'embedded-asset-block': {
                const fields = node.data?.target?.fields;
                if (fields?.file) {
                    return `<img
                            src="https://${fields.file.url || ''}"
                            height="${fields.file.details?.image?.height || 0}"
                            width="${fields.file.details?.image?.width || 0}"
                            alt="${fields.description || ''}"
                          />`;
                }
                return '';
            }

            case 'hyperlink': {
                const uri = node.data?.uri;
                return `<a href="${uri || ''}">${node.content?.map((child: RichTextNode) => this.processRichTextNode(child)).join('') || ''}</a>`;
            }

            default:
                return (
                    node.content
                        ?.map((child: RichTextNode) =>
                            this.processRichTextNode(child)
                        )
                        .join('') || ''
                );
        }
    }

    private resolveLinks<T extends ContentfulEntryContract>(
        entry: T,
        assets: ContentfulAssetContract[],
        entries: ContentfulEntryContract[]
    ): ContentfulEntryFromContract<T> {
        const fields = Object.keys(
            entry.fields
        ).reduce<ContentfulEntryContractFields>((processed, key) => {
            const field = entry.fields[key];
            if (isAssetLink(field)) {
                const asset = this.resolveAssetLink(field, assets);
                if (asset) {
                    processed[key] = asset;
                }
            } else if (isEntryLink(field)) {
                const subEntry = this.resolveEntryLink(field, assets, entries);
                if (subEntry) {
                    processed[key] = subEntry;
                }
            } else if (isRichTextNodeContract(field)) {
                const node = this.resolveRichTextNodes(field, assets);
                if (node) {
                    processed[key] = node;
                }
            } else if (Array.isArray(field)) {
                const hasLinks = field.some(
                    (item: string | LinkObject) =>
                        isAssetLink(item) || isEntryLink(item)
                );

                if (hasLinks) {
                    processed[key] = field.reduce<
                        ContentfulEntry<ContentfulAssetFields>[]
                    >((items, item) => {
                        if (isAssetLink(item)) {
                            const asset = this.resolveAssetLink(item, assets);
                            if (asset) {
                                items.push(asset);
                            }
                        } else if (isEntryLink(item)) {
                            const subEntry = this.resolveEntryLink(
                                item,
                                assets,
                                entries
                            );
                            if (subEntry) {
                                items.push(subEntry);
                            }
                        }
                        return items;
                    }, []);
                } else {
                    processed[key] = field as string[];
                }
            } else if (field) {
                processed[key] = field;
            }

            return processed;
        }, {});

        return {
            ...entry,
            fields,
        } as unknown as ContentfulEntryFromContract<T>;
    }

    private resolveAssetLink(
        link: AssetLink,
        assets: ContentfulAssetContract[]
    ): ContentfulAssetContract | undefined {
        const resolved = assets.find((item) => item.sys.id === link.sys.id);

        if (!resolved) {
            return;
        }

        return resolved;
    }

    private resolveEntryLink(
        link: EntryLink,
        assets: ContentfulAssetContract[],
        entries: ContentfulEntryContract[]
    ): ContentfulEntry<ContentfulAssetFields> | undefined {
        const entry = (entries ?? []).find(
            (item) => item.sys.id === link.sys.id
        );

        if (!entry) {
            return;
        }

        return this.resolveLinks(
            entry,
            assets,
            entries
        ) as ContentfulEntry<ContentfulAssetFields>;
    }

    private resolveRichTextNodes(
        node: RichTextNodeContract,
        assets: ContentfulAssetContract[]
    ): RichTextNode {
        const content = (node.content ?? []).map<RichTextNode>((subNode) => {
            if (subNode.content?.length) {
                return this.resolveRichTextNodes(subNode, assets);
            } else if (isAssetLink(subNode.data?.target)) {
                const target = this.resolveAssetLink(
                    subNode.data.target,
                    assets
                );
                return {
                    ...subNode,
                    data: { ...subNode.data, target },
                } as RichTextNode;
            } else {
                return subNode as RichTextNode;
            }
        });

        return { ...node, content } as RichTextNode;
    }
}
